class ToggleSection extends HTMLElement {
  constructor() {
    super()
    this.onToggle = this.onToggle.bind(this)
    this.panelHandlers = new WeakMap()
  }

  connectedCallback() {
    this.init()
  }

  disconnectedCallback() {
    this.buttons?.forEach((button) => {
      button.removeEventListener('click', this.onToggle)
    })
  }

  init() {
    this.buttons = this.querySelectorAll('[data-toggle="toggle"]')
    this.buttons.forEach((button) => {
      const header = button.closest('.section-header')
      this.getPanel(header)
      button.addEventListener('click', this.onToggle)
      this.sync(button, true, true)
    })
    this.classList.add('is-ready')
  }

  onToggle(event) {
    event.preventDefault()
    const button = event.currentTarget
    const header = button.closest('.section-header')
    const collapsed = !header?.classList.contains('is-collapsed')
    this.sync(button, collapsed)
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  getPanel(header) {
    if (!header) {
      return null
    }

    const next = header.nextElementSibling
    if (next?.classList.contains('toggle-section__panel')) {
      return next
    }

    const content = []
    let el = next
    while (el && !el.classList.contains('section-header') && !el.classList.contains('toggle-section__panel')) {
      content.push(el)
      el = el.nextElementSibling
    }

    if (!content.length) {
      return null
    }

    const panel = document.createElement('div')
    panel.className = 'toggle-section__panel'
    const inner = document.createElement('div')
    inner.className = 'toggle-section__panel-inner'
    panel.append(inner)
    header.after(panel)
    inner.append(...content)
    return panel
  }

  animatePanel(panel, collapsed, instant = false) {
    const previousHandler = this.panelHandlers.get(panel)
    if (previousHandler) {
      panel.removeEventListener('transitionend', previousHandler)
      this.panelHandlers.delete(panel)
    }

    if (instant || this.prefersReducedMotion()) {
      panel.style.height = collapsed ? '0px' : ''
      panel.style.opacity = collapsed ? '0' : ''
      panel.style.overflow = collapsed ? 'hidden' : ''
      return
    }

    const startHeight = panel.getBoundingClientRect().height
    panel.style.overflow = 'hidden'
    panel.style.transition = 'none'

    let endHeight = 0
    if (collapsed) {
      panel.style.height = `${startHeight}px`
      panel.style.opacity = '1'
    } else {
      panel.style.height = 'auto'
      endHeight = panel.scrollHeight
      panel.style.height = `${startHeight}px`
      panel.style.opacity = startHeight === 0 ? '0' : '1'
    }

    panel.offsetHeight
    panel.style.transition = ''
    panel.style.height = `${endHeight}px`
    panel.style.opacity = collapsed ? '0' : '1'

    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') {
        return
      }

      panel.removeEventListener('transitionend', onEnd)
      this.panelHandlers.delete(panel)

      if (!collapsed) {
        panel.style.height = ''
        panel.style.overflow = ''
        panel.style.opacity = ''
      }
    }

    this.panelHandlers.set(panel, onEnd)
    panel.addEventListener('transitionend', onEnd)
  }

  sync(button, collapsed, instant = false) {
    const header = button.closest('.section-header')
    if (!header) {
      return
    }

    const panel = this.getPanel(header)
    header.classList.toggle('is-collapsed', collapsed)
    button.setAttribute('aria-expanded', String(!collapsed))
    const label = collapsed ? button.dataset.labelCollapsed : button.dataset.labelExpanded
    if (label) {
      button.setAttribute('aria-label', label)
    }

    if (panel) {
      panel.setAttribute('aria-hidden', String(collapsed))
      panel.inert = collapsed
      this.animatePanel(panel, collapsed, instant)
    }
  }
}

if (!customElements.get('toggle-section')) {
  customElements.define('toggle-section', ToggleSection)
}
